import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import { colors } from "../../theme/colors";
import { getMapMetaById } from "../../constants/mapMeta";
import {
    MAP_VOTE_PENDING_HEADING,
    MAP_VOTE_PENDING_HINT,
} from "../../constants/mapVote";
import { toLobbyPath } from "../../universal/v2Routes";
import { remainingSecondsFromExpiresAt } from "../../utils/matchAcceptance";
import LobbyMainCta from "../lobby/LobbyMainCta";
import {
    MOBILE_NAV_FAB_BOTTOM,
    MOBILE_NAV_MEDIA,
} from "../layout/layoutConstants";

const MotionBox = motion(Box);

/**
 * Compact global match-ready cue for routes outside the lobby.
 */
export default function MatchReadyNotification({ matchAcceptance, onAcceptMatch }) {
    const history = useHistory();
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    const mapId = matchAcceptance?.map || null;
    const mapVotePending = Boolean(matchAcceptance?.mapVotePending);
    const mapMeta = getMapMetaById(mapId);
    const hasAccepted = Boolean(
        matchAcceptance?.hasAccepted || matchAcceptance?.accepted
    );
    const allAccepted = Boolean(matchAcceptance?.allAccepted);

    const [timeLeft, setTimeLeft] = useState(() => {
        const fromExpires = remainingSecondsFromExpiresAt(
            matchAcceptance?.expiresAt
        );
        if (fromExpires != null) return fromExpires;
        return Number(matchAcceptance?.timeoutSeconds) || 0;
    });

    useEffect(() => {
        const tick = () => {
            const fromExpires = remainingSecondsFromExpiresAt(
                matchAcceptance?.expiresAt
            );
            if (fromExpires != null) {
                setTimeLeft(fromExpires);
                return;
            }
            setTimeLeft((prev) => Math.max(0, prev - 1));
        };

        tick();
        const id = window.setInterval(tick, 250);
        return () => window.clearInterval(id);
    }, [matchAcceptance?.expiresAt, matchAcceptance?.matchId]);

    if (!matchAcceptance?.matchId) return null;

    const isUrgent = timeLeft > 0 && timeLeft <= 8;
    const openLobby = () => {
        history.push(toLobbyPath(history.location?.pathname));
    };

    const handleAcceptMatch = (event) => {
        event.stopPropagation();
        onAcceptMatch?.();
    };

    return (
        <MotionBox
            initial={{ opacity: 0, y: 18, scale: 0.96, x: "-50%" }}
            animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
            exit={{ opacity: 0, y: 32, scale: 0.96, x: "-50%" }}
            transition={{
                delay: 0.3,
                duration: 0.32,
                ease: [0.22, 1, 0.36, 1],
            }}
            sx={{
                position: "fixed",
                left: {
                    xs: "50%",
                    md: "calc(50% + 46px)",
                    xl: "calc(50% + 51px)",
                },
                bottom: isMobile
                    ? MOBILE_NAV_FAB_BOTTOM
                    : { xs: "1.25rem", xl: "1.5rem" },
                width: { xs: "calc(100% - 1.4rem)", sm: "auto" },
                zIndex: 1600,
            }}
        >
            <Box
                onClick={openLobby}
                role="button"
                tabIndex={0}
                aria-label="Match ready"
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openLobby();
                    }
                }}
                sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: "0.65rem", sm: "0.8rem" },
                    width: { xs: "100%", md: "auto" },
                    minWidth: { xs: 0, sm: "28rem", md: "31rem" },
                    px: { xs: "0.8rem", sm: "1.15rem" },
                    height: { xs: "4rem", sm: "4.25rem", xl: "4.4rem" },
                    borderRadius: "14px",
                    background:
                        "linear-gradient(180deg, rgba(29,26,22,0.985), rgba(11,11,14,0.99))",
                    border: isUrgent
                        ? "1px solid rgba(255,88,88,0.35)"
                        : "1px solid rgba(238,174,67,0.34)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    boxShadow: isUrgent
                        ? `
                            0 18px 48px rgba(0,0,0,0.55),
                            0 0 32px rgba(255,88,88,0.18)
                        `
                        : `
                            0 18px 48px rgba(0,0,0,0.55),
                            0 0 32px rgba(232,163,61,0.12)
                        `,
                    transition:
                        "transform 200ms cubic-bezier(0.22,1,0.36,1), border-color 200ms ease, background 200ms ease, box-shadow 200ms ease",
                    "&:hover": {
                        transform: "translateY(-1px)",
                        borderColor: isUrgent
                            ? "rgba(255,88,88,0.52)"
                            : "rgba(242,183,82,0.5)",
                        background:
                            "linear-gradient(180deg, rgba(36,30,22,0.99), rgba(13,12,12,0.99))",
                    },
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        left: "1.1rem",
                        right: "1.1rem",
                        top: 0,
                        height: "1px",
                        background: `
                            linear-gradient(
                                90deg,
                                transparent,
                                ${isUrgent ? "rgba(255,88,88,0.7)" : "rgba(232,163,61,0.75)"},
                                transparent
                            )
                        `,
                    },
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <Typography
                        sx={{
                            color: colors.text.primary,
                            fontSize: { xs: "0.61rem", sm: "0.68rem" },
                            fontWeight: 750,
                            letterSpacing: "0.13em",
                            lineHeight: 1,
                            textTransform: "uppercase",
                        }}
                    >
                        {allAccepted
                            ? "Match Confirmed"
                            : hasAccepted
                              ? "Match Accepted"
                              : "Match Ready"}
                    </Typography>
                    <Typography
                        sx={{
                            mt: "0.25rem",
                            color: colors.text.primary,
                            fontSize: { xs: "0.7rem", sm: "0.76rem" },
                            fontWeight: 650,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {mapVotePending
                            ? MAP_VOTE_PENDING_HEADING
                            : mapMeta?.display || mapId || "Competitive queue"}
                        {" · "}
                        {allAccepted
                            ? mapVotePending
                                ? MAP_VOTE_PENDING_HINT
                                : "Allocating server"
                            : hasAccepted
                              ? "Waiting for players"
                              : "Confirm your spot"}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: { xs: "none", sm: "block" },
                        width: "1px",
                        height: "1.85rem",
                        ml: "auto",
                        backgroundColor: colors.border.default,
                    }}
                />

                <Box
                    sx={{
                        width: { xs: "6.4rem", sm: "7.6rem" },
                        flexShrink: 0,
                    }}
                >
                    <LobbyMainCta
                        label={
                            allAccepted
                                ? "Confirmed"
                                : hasAccepted
                                  ? "Accepted"
                                  : "Accept"
                        }
                        onClick={handleAcceptMatch}
                        disabled={hasAccepted || allAccepted}
                        variant={
                            hasAccepted || allAccepted ? "accepted" : "default"
                        }
                        sx={{
                            width: "100%",
                            height: { xs: "2.2rem", sm: "2.5rem" },
                        }}
                        labelSx={{
                            fontSize: { xs: "0.62rem", sm: "0.7rem" },
                            letterSpacing: "0.11em",
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        minWidth: { xs: "2.45rem", sm: "2.8rem" },
                        textAlign: "right",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "'Orbitron', sans-serif",
                            fontSize: { xs: "0.8rem", sm: "0.94rem" },
                            fontWeight: 800,
                            color: isUrgent
                                ? colors.state.danger.main
                                : colors.primary.main,
                            fontVariantNumeric: "tabular-nums",
                            letterSpacing: "0.035em",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {Math.max(0, timeLeft)}s
                    </Typography>
                </Box>
            </Box>
        </MotionBox>
    );
}

MatchReadyNotification.propTypes = {
    matchAcceptance: PropTypes.shape({
        matchId: PropTypes.string,
        map: PropTypes.string,
        mapVotePending: PropTypes.bool,
        expiresAt: PropTypes.number,
        timeoutSeconds: PropTypes.number,
        hasAccepted: PropTypes.bool,
        accepted: PropTypes.bool,
        allAccepted: PropTypes.bool,
    }),
    onAcceptMatch: PropTypes.func,
};
